import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { config } from '../../../config';
import {
  BookOpen,
  FileText,
  Edit3,
  Trash2,
  Plus,
  Save,
  X,
  ArrowLeft,
  Sigma,
  Divide,
  Square,
  Superscript,
  Subscript,
  Bold,
  Italic,
  Underline,
  List,
  Link,
  Image,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Quote,
  Code,
  Type
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const apiUrl = config.apiUrl;

const CreateNote = ({ onBack }) => {
  const [notes, setNotes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [formData, setFormData] = useState({
    subject_id: '',
    module_id: '',
    lesson_id: '',
    title: '',
    content: ''
  });
  const [editingNote, setEditingNote] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const quillRef = useRef(null);

  // React Quill modules configuration
  const quillModules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        // Custom handlers can be added here if needed
      }
    },
    clipboard: {
      matchVisual: false,
    }
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  // Insert math symbol at cursor position
  const insertMathSymbol = (symbol) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      if (range) {
        quill.insertText(range.index, symbol);
        quill.setSelection(range.index + symbol.length);
      } else {
        quill.insertText(quill.getLength(), symbol);
      }
    }
  };

  // Insert matrix template
  const insertMatrix = () => {
    const matrix = '\\begin{bmatrix} a & b \\\\ c & d \\end{bmatrix}';
    insertMathSymbol(matrix);
  };

  // Custom toolbar component for math symbols
  const CustomToolbar = () => (
    <div id="toolbar" className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
      <span className="font-medium text-sm text-gray-700 mr-2">গাণিতিক চিহ্ন:</span>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="ভগ্নাংশ যোগ করুন"
        onClick={() => insertMathSymbol('\\frac{a}{b}')}
      >
        <Divide className="h-3 w-3 mr-1" />
        ভগ্নাংশ
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="গুণ করুন"
        onClick={() => insertMathSymbol('\\times')}
      >
        <span className="text-sm font-bold mr-1">×</span>
        গুণ
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="বর্গমূল যোগ করুন"
        onClick={() => insertMathSymbol('\\sqrt{x}')}
      >
        <Square className="h-3 w-3 mr-1" />
        বর্গমূল
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="সুপারস্ক্রিপ্ট যোগ করুন"
        onClick={() => insertMathSymbol('x^{2}')}
      >
        <Superscript className="h-3 w-3 mr-1" />
        সুপারস্ক্রিপ্ট
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="সাবস্ক্রিপ্ট যোগ করুন"
        onClick={() => insertMathSymbol('x_{2}')}
      >
        <Subscript className="h-3 w-3 mr-1" />
        সাবস্ক্রিপ্ট
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="ম্যাট্রিক্স যোগ করুন"
        onClick={insertMatrix}
      >
        <Sigma className="h-3 w-3 mr-1" />
        ম্যাট্রিক্স
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="সমষ্টি যোগ করুন"
        onClick={() => insertMathSymbol('\\sum_{i=1}^{n}')}
      >
        <span className="text-xs font-bold mr-1">∑</span>
        সমষ্টি
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="ইন্টিগ্রাল যোগ করুন"
        onClick={() => insertMathSymbol('\\int_{a}^{b} f(x) dx')}
      >
        <span className="text-xs font-bold mr-1">∫</span>
        ইন্টিগ্রাল
      </button>
    </div>
  );

  // Fetch all notes and subjects
  useEffect(() => {
    fetchNotes();
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (formData.subject_id) {
      fetchModulesBySubject(formData.subject_id);
    } else {
      setModules([]);
      setLessons([]);
      setFormData(prev => ({ ...prev, module_id: '', lesson_id: '' }));
    }
  }, [formData.subject_id]);

  useEffect(() => {
    if (formData.module_id) {
      fetchLessonsByModule(formData.module_id);
    } else {
      setLessons([]);
      setFormData(prev => ({ ...prev, lesson_id: '' }));
    }
  }, [formData.module_id]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${apiUrl}/notes`);
      setNotes(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError('নোট লোড করতে সমস্যা হয়েছে');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${apiUrl}/subjects`);
      setSubjects(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching subjects:', err);
      setError('বিষয়গুলি লোড করতে সমস্যা হয়েছে');
    }
  };

  const fetchModulesBySubject = async (subjectId) => {
    setLoadingModules(true);
    try {
      const response = await axios.get(`${apiUrl}/subjects/${subjectId}/modules`);
      setModules(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching modules:', err);
      setError('মডিউলগুলি লোড করতে সমস্যা হয়েছে');
    } finally {
      setLoadingModules(false);
    }
  };

  const fetchLessonsByModule = async (moduleId) => {
    setLoadingLessons(true);
    try {
      const response = await axios.get(`${apiUrl}/modules/${moduleId}/lessons`);
      setLessons(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching lessons:', err);
      setError('লেসনগুলি লোড করতে সমস্যা হয়েছে');
    } finally {
      setLoadingLessons(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({
      ...prev,
      content: content
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingNote) {
        // Update existing note
        await axios.put(`${apiUrl}/notes/${editingNote.id}`, formData);
        setSuccess('নোট সফলভাবে আপডেট হয়েছে!');
      } else {
        // Create new note
        await axios.post(`${apiUrl}/notes`, formData);
        setSuccess('নোট সফলভাবে তৈরি হয়েছে!');
      }
      
      fetchNotes(); // Refresh the list
      resetForm();
    } catch (err) {
      console.error('Error saving note:', err);
      setError(err.response?.data?.message || 'নোট সংরক্ষণ করতে সমস্যা হয়েছে। আবার চেষ্টা করুন।');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setFormData({
      subject_id: note.subject_id || '',
      module_id: note.module_id || '',
      lesson_id: note.lesson_id || '',
      title: note.title,
      content: note.content
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('আপনি কি এই নোটটি মুছতে চান?')) {
      try {
        await axios.delete(`${apiUrl}/notes/${id}`);
        setSuccess('নোট সফলভাবে মুছে ফেলা হয়েছে!');
        fetchNotes(); // Refresh the list
      } catch (err) {
        console.error('Error deleting note:', err);
        setError('নোট মুছতে সমস্যা হয়েছে');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      subject_id: '',
      module_id: '',
      lesson_id: '',
      title: '',
      content: ''
    });
    setEditingNote(null);
    setError(null);
    setSuccess(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                  <FileText className="h-6 w-6 mr-2 text-blue-600" />
                  নোট ম্যানেজমেন্ট
                </h1>
                <p className="text-gray-600 text-sm">নতুন নোট তৈরি এবং ব্যবস্থাপনা করুন</p>
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <X className="h-5 w-5 text-red-600 mr-3" />
              <span className="text-red-800">{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
              <Save className="h-5 w-5 text-green-600 mr-3" />
              <span className="text-green-800">{success}</span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Note Form */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-6 text-center flex items-center justify-center">
                {editingNote ? (
                  <>
                    <Edit3 className="h-5 w-5 mr-2 text-yellow-600" />
                    নোট সম্পাদনা করুন
                  </>
                ) : (
                  <>
                    <Plus className="h-5 w-5 mr-2 text-green-600" />
                    নতুন নোট তৈরি করুন
                  </>
                )}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Subject, Module, Lesson Selection */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <BookOpen className="h-4 w-4 mr-2 text-blue-600" />
                      বিষয় *
                    </label>
                    <select
                      name="subject_id"
                      value={formData.subject_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">বিষয় নির্বাচন করুন</option>
                      {subjects.map(subject => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      মডিউল
                    </label>
                    <select
                      name="module_id"
                      value={formData.module_id}
                      onChange={handleChange}
                      disabled={!formData.subject_id || loadingModules}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                    >
                      <option value="">মডিউল নির্বাচন করুন</option>
                      {loadingModules ? (
                        <option value="">লোড হচ্ছে...</option>
                      ) : (
                        modules.map(module => (
                          <option key={module.id} value={module.id}>
                            {module.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      লেসন
                    </label>
                    <select
                      name="lesson_id"
                      value={formData.lesson_id}
                      onChange={handleChange}
                      disabled={!formData.module_id || loadingLessons}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                    >
                      <option value="">লেসন নির্বাচন করুন</option>
                      {loadingLessons ? (
                        <option value="">লোড হচ্ছে...</option>
                      ) : (
                        lessons.map(lesson => (
                          <option key={lesson.id} value={lesson.id}>
                            {lesson.title}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    শিরোনাম *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    maxLength="255"
                    placeholder="নোটের শিরোনাম লিখুন"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    বিষয়বস্তু *
                  </label>
                  
                  {/* Custom Math Toolbar */}
                  <CustomToolbar />
                  
                  <ReactQuill
                    ref={quillRef}
                    value={formData.content || ""}
                    onChange={handleContentChange}
                    modules={quillModules}
                    formats={quillFormats}
                    theme="snow"
                    className="h-64 mb-12"
                    placeholder="নোটের বিষয়বস্তু লিখুন..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-16">
                  {editingNote && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      বাতিল
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        সংরক্ষণ হচ্ছে...
                      </>
                    ) : editingNote ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        আপডেট করুন
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        তৈরি করুন
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Notes List */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-6 text-center flex items-center justify-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                সকল নোট
              </h2>
              
              {notes.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p>কোনো নোট পাওয়া যায়নি। প্রথম নোট তৈরি করুন!</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {[...notes].reverse().map(note => (
                    <div key={note.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-lg font-semibold text-gray-800">{note.title}</h3>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          {note.subject?.name}
                        </span>
                      </div>
                      
                      {note.module && (
                        <p className="text-sm text-gray-600 mb-1">
                          মডিউল: {note.module.name}
                        </p>
                      )}
                      
                      {note.lesson && (
                        <p className="text-sm text-gray-600 mb-2">
                          লেসন: {note.lesson.title}
                        </p>
                      )}
                      
                      <div 
                        className="text-gray-600 mb-4 line-clamp-3 quill-content"
                        dangerouslySetInnerHTML={{ __html: note.content }} 
                      />
                      
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(note)}
                          className="px-3 py-1 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 transition-colors flex items-center"
                        >
                          <Edit3 className="h-3 w-3 mr-1" />
                          সম্পাদনা
                        </button>
                        <button
                          onClick={() => handleDelete(note.id)}
                          className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors flex items-center"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          মুছুন
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for Quill content preview */}
      <style jsx>{`
        .quill-content {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
        }
        
        .quill-content p {
          margin: 0;
          padding: 0;
        }
        
        .quill-content img {
          max-width: 100%;
          height: auto;
        }
      `}</style>
    </div>
  );
};

export default CreateNote;